import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useArticleTypeBySlug, useArticlesByType } from "@/hooks/useArticles";
import { ArticleCard } from "@/components/ArticleCard";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ScrollToTop from "@/components/ScrollToTop";
import EmptyState from "@/components/EmptyState";
import { ChevronRight } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Article type-specific SEO descriptions
const typeDescriptions: Record<string, string> = {
  "guide": "Komplette guides til dit sabbatår. Få detaljeret vejledning og step-by-step instruktioner til alt fra rejseplanlægning til økonomi.",
  "tips": "Praktiske tips og tricks til dit sabbatår. Få gode råd fra erfarne sabbatår-rejsende.",
  "erfaring": "Læs personlige erfaringer og beretninger fra unge, der har taget et sabbatår. Få inspiration fra virkelige oplevelser.",
  "mulighed": "Udforsk muligheder for dit sabbatår. Fra frivilligt arbejde til arbejde i udlandet - find den rigtige vej for dig.",
  "debat": "Debatindlæg og holdninger om sabbatår. Læs forskellige perspektiver på at tage et sabbatår.",
  "oversigt": "Overblik og samlede ressourcer om sabbatår. Få det store perspektiv på dine muligheder.",
  "praktisk": "Praktisk information om sabbatår. Alt det praktiske du skal vide om økonomi, forsikring, og planlægning."
};

export default function ArticleTypePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: articleType, isLoading: typeLoading } = useArticleTypeBySlug(slug || "");
  const { data: articles, isLoading: articlesLoading } = useArticlesByType(slug || "");

  // Scroll to top when type changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const isLoading = typeLoading || articlesLoading;

  const colorAccents = ["coral", "marine", "sage", "sky", "lavender"];

  // Get SEO description for this type
  const seoDescription = useMemo(() => {
    if (!slug) return null;
    return typeDescriptions[slug] || `Artikler med emnet "${articleType?.name || slug}"`;
  }, [slug, articleType]);

  // Build breadcrumbs for SEO
  const breadcrumbs = useMemo(() => {
    const items = [{ name: "Forside", url: "https://mysabbat.dk" }];
    if (articleType) {
      items.push({
        name: articleType.name,
        url: `https://mysabbat.dk/emne/${articleType.slug}`
      });
    }
    return items;
  }, [articleType]);

  return (
    <>
      <SEOHead
        title={articleType ? `${articleType.name} artikler | MySabbat` : "Emne | MySabbat"}
        description={seoDescription || "Udforsk artikler om sabbatår"}
        path={`/emne/${slug}`}
        breadcrumbs={breadcrumbs}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <ScrollToTop />
        
        <main className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Forside</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>{articleType?.name || "Emne"}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Type header */}
          <div className="mb-8">
            {isLoading ? (
              <>
                <div className="h-8 w-64 bg-muted rounded animate-pulse mb-2" />
                <div className="h-5 w-96 bg-muted rounded animate-pulse" />
              </>
            ) : articleType ? (
              <>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {articleType.name}
                </h1>
                {seoDescription && (
                  <p className="text-muted-foreground leading-relaxed border-l-4 border-primary/30 pl-4 bg-muted/30 py-3 rounded-r-lg">
                    {seoDescription}
                  </p>
                )}
              </>
            ) : (
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Emne ikke fundet
              </h1>
            )}
          </div>

          {/* Articles grid */}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card rounded-2xl animate-pulse border border-border">
                  <div className="aspect-[16/10] bg-muted rounded-t-2xl" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 w-20 bg-muted rounded-full" />
                    <div className="h-6 w-3/4 bg-muted rounded" />
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles && articles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <ArticleCard 
                  key={article.id} 
                  article={article} 
                  colorAccent={colorAccents[index % colorAccents.length]}
                />
              ))}
            </div>
          ) : (
            <EmptyState 
              type="category"
              ctaText="Se alle artikler"
              ctaLink="/"
            />
          )}

          {/* Back link */}
          <div className="mt-8 pt-8 border-t border-border">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Tilbage til forsiden
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
